import type { AIMeasurement } from '@/models/aiScan';

/** Pick which of two AI measurements sharing a canonical to display.
 *
 *  EchoMeasure can measure one field several ways (LV EF from biplane, A4C and
 *  A2C; LA area from six view/phase combinations), and they all arrive under the
 *  same canonical. The backend ranks them via `priority` but never filters —
 *  the viewer draws every variant's annotation — so the choice is ours:
 *  lowest priority wins, unranked sorts last, `privateCode` breaks ties.
 *
 *  The tiebreak is not cosmetic. AI measurements are deleted and re-created on
 *  every report refetch, so an all-unranked group ordered by payload alone could
 *  show a different value after a refetch with nothing explaining the change.
 *
 *  Mirrors `_prefer_ai_variant` in the backend's `exports/mixins.py`, which
 *  makes the same choice for the exported PDF.
 *
 *  The worksheet keeps its own copy in `features/report` — deliberately not
 *  shared. That one serves a five-state dot model; this one exists to match the
 *  printed PDF, and the two are free to diverge. Fold them together only if
 *  that stops being true. */
export function preferAIVariant(
  current: AIMeasurement | undefined,
  candidate: AIMeasurement
): AIMeasurement {
  if (!current) return candidate;
  const rank = (m: AIMeasurement): [number, string] => [
    m.priority ?? Number.MAX_SAFE_INTEGER,
    m.privateCode ?? '',
  ];
  const [currentPriority, currentCode] = rank(current);
  const [candidatePriority, candidateCode] = rank(candidate);
  if (candidatePriority !== currentPriority)
    return candidatePriority < currentPriority ? candidate : current;
  return candidateCode < currentCode ? candidate : current;
}
